import {
  CompanyType,
  PlanningCategory,
  PlanningClassification,
  allPlanningCategories,
  allPlanningClassifications,
} from '@leadgen/models/client'

export {
  categoriesByClassification,
  filterPlanningCategoriesForClassifications,
  getPlanningCategoriesForClassifications,
} from '@leadgen/models/client'

export type PlanningTaxonomyOptionMetadata = {
  title: string
  description: string
}

export type PlanningClassificationGroupConfig = {
  id: string
  title: string
  searchPlaceholder: string
  options: PlanningClassification[]
}

export type PlanningCategoryGroupConfig = {
  id: string
  title: string
  description?: string
  options: PlanningCategory[]
}

const unique = <T extends string>(values: readonly T[]) => Array.from(new Set(values))

const withCategories = (...groups: PlanningCategory[][]): PlanningCategory[] =>
  unique(groups.flat()).filter(category => allPlanningCategories.includes(category))

export const planningClassificationMap: Record<
  PlanningClassification,
  PlanningTaxonomyOptionMetadata
> = {
  houses: {
    title: 'Houses',
    description: 'Detached, semi-detached, and terraced residential houses.',
  },
  'flats-apartments': {
    title: 'Flats / apartments',
    description: 'Blocks of flats, apartments, maisonettes, and similar residential units.',
  },
  hmos: {
    title: 'HMOs',
    description: 'Houses in multiple occupation and shared residential accommodation.',
  },
  schools: {
    title: 'Schools',
    description: 'Primary, secondary, sixth-form, and specialist school sites.',
  },
  'training-centres': {
    title: 'Training centres',
    description: 'Training centres, adult learning facilities, and similar education sites.',
  },
  colleges: {
    title: 'Colleges',
    description: 'Further education colleges and college campus buildings.',
  },
  universities: {
    title: 'Universities',
    description: 'University buildings, campuses, and higher education facilities.',
  },
  'kindergartens-nurseries-creche': {
    title: 'Nurseries / creche',
    description: 'Kindergartens, nurseries, creches, and early years education sites.',
  },
  'community-centres': {
    title: 'Community centres',
    description: 'Community halls, local centres, and amenity buildings.',
  },
  libraries: {
    title: 'Libraries',
    description: 'Libraries, archives, and public reading rooms.',
  },
  'civic-government-buildings': {
    title: 'Civic / government',
    description: 'Council, government, civic, and public administration buildings.',
  },
  'religious-buildings': {
    title: 'Religious buildings',
    description: 'Churches, mosques, temples, chapels, and places of worship.',
  },
  'emergency-justice-buildings': {
    title: 'Emergency / justice',
    description: 'Police, fire, ambulance, court, prison, and justice-related buildings.',
  },
  museums: {
    title: 'Museums',
    description: 'Museums and heritage visitor buildings.',
  },
  galleries: {
    title: 'Galleries',
    description: 'Art galleries and exhibition gallery buildings.',
  },
  'public-toilets': {
    title: 'Public toilets',
    description: 'Public toilet buildings and facilities.',
  },
  hospitals: {
    title: 'Hospitals',
    description: 'Hospitals and acute healthcare sites.',
  },
  'health-clinics-surgeries': {
    title: 'Health clinics / surgeries',
    description: 'GP surgeries, health centres, clinics, and medical practices.',
  },
  'dental-clinics-surgeries': {
    title: 'Dental clinics / surgeries',
    description: 'Dental practices, orthodontic clinics, and dental surgeries.',
  },
  'care-homes': {
    title: 'Care homes',
    description: 'Care homes, nursing homes, assisted living, and residential care sites.',
  },
  'medical-research-facilities': {
    title: 'Medical research',
    description: 'Medical laboratories, research buildings, and clinical research facilities.',
  },
  'veterinary-clinics': {
    title: 'Veterinary clinics',
    description: 'Veterinary surgeries, animal clinics, and vet practices.',
  },
  pharmacies: {
    title: 'Pharmacies',
    description: 'Pharmacies and chemist premises.',
  },
  shops: {
    title: 'Shops',
    description: 'Individual shops and retail premises.',
  },
  'shopping-centres': {
    title: 'Shopping centres',
    description: 'Shopping centres, malls, and multi-unit retail destinations.',
  },
  supermarkets: {
    title: 'Supermarkets',
    description: 'Supermarkets, food stores, and large-format grocery retail.',
  },
  offices: {
    title: 'Offices',
    description: 'Office buildings, workspaces, and business premises.',
  },
  'banks-building-societies': {
    title: 'Banks / building societies',
    description: 'Banks, building societies, and financial service branches.',
  },
  showrooms: {
    title: 'Showrooms',
    description: 'Car showrooms, display showrooms, and service retail premises.',
  },
  hotels: {
    title: 'Hotels',
    description: 'Hotels, motels, and serviced accommodation.',
  },
  'guest-houses': {
    title: 'Guest houses',
    description: 'Guest houses, B&Bs, and small visitor accommodation.',
  },
  restaurants: {
    title: 'Restaurants',
    description: 'Restaurants and sit-down food premises.',
  },
  cafes: {
    title: 'Cafes',
    description: 'Cafes, coffee shops, and casual food premises.',
  },
  'fast-food': {
    title: 'Fast food',
    description: 'Fast food, takeaway, and quick-service food premises.',
  },
  'pubs-bars': {
    title: 'Pubs / bars',
    description: 'Pubs, bars, wine bars, and similar licensed premises.',
  },
  'night-clubs': {
    title: 'Night clubs',
    description: 'Night clubs and late-night entertainment venues.',
  },
  gyms: {
    title: 'Gyms',
    description: 'Gyms, fitness studios, and exercise facilities.',
  },
  'leisure-centres': {
    title: 'Leisure centres',
    description: 'Leisure centres, sports halls, swimming centres, and recreation buildings.',
  },
  'salons-barbers': {
    title: 'Salons / barbers',
    description: 'Hairdressers, barbers, beauty salons, and treatment rooms.',
  },
  'games-venues': {
    title: 'Games venues',
    description: 'Bingo halls, snooker halls, bowling alleys, betting shops, and arcades.',
  },
  'conference-exhibition-venues': {
    title: 'Conference / exhibition venues',
    description: 'Conference centres, exhibition venues, and event buildings.',
  },
  stadiums: {
    title: 'Stadiums',
    description: 'Stadiums, arenas, spectator stands, and major sports venues.',
  },
  'outdoor-recreational-areas': {
    title: 'Outdoor recreational areas',
    description: 'Outdoor sports, play, skate, park, and recreation areas.',
  },
  'camp-sites-holiday-villages': {
    title: 'Camp sites / holiday villages',
    description: 'Camp sites, caravan parks, holiday villages, and outdoor visitor sites.',
  },
  farms: {
    title: 'Farms',
    description:
      'Farms, agricultural holdings, farmyards, agricultural land, fields, and non-building farm infrastructure.',
  },
  'animal-buildings': {
    title: 'Animal buildings',
    description: 'Agricultural, equestrian, livestock, kennel, and animal buildings.',
  },
  'warehouses-storage-buildings': {
    title: 'Warehouses / storage',
    description: 'Warehouses, storage buildings, depots, and distribution storage.',
  },
  workshops: {
    title: 'Workshops',
    description: 'Workshops and small industrial workspaces.',
  },
  factories: {
    title: 'Factories',
    description: 'Factories, production buildings, and manufacturing premises.',
  },
  'industrial-units': {
    title: 'Industrial units',
    description: 'Light industrial units and general industrial premises.',
  },
  'distribution-depots': {
    title: 'Distribution depots',
    description: 'Distribution depots, logistics hubs, and loading facilities.',
  },
  'vehicle-maintenance': {
    title: 'Vehicle maintenance',
    description: 'Vehicle maintenance, repair, and servicing buildings.',
  },
  'data-centres': {
    title: 'Data centres',
    description: 'Data centres and IT processing facilities.',
  },
  'cold-stores-freezing-plants': {
    title: 'Cold stores / freezing plants',
    description: 'Cold stores, freezer buildings, and temperature-controlled facilities.',
  },
}

export const planningCategoryMap: Record<PlanningCategory, PlanningTaxonomyOptionMetadata> = {
  conversions: {
    title: 'Conversions',
    description: 'Physical conversion works such as loft, basement, garage, or barn conversions.',
  },
  extensions: {
    title: 'Extensions',
    description: 'Extensions to existing buildings, including single-storey and multi-storey work.',
  },
  'new-build': {
    title: 'New build',
    description: 'Construction of one or more new buildings or structures.',
  },
  'exterior-finish': {
    title: 'Exterior finish',
    description: 'External finish works such as render, cladding, panelling, or facade changes.',
  },
  balconies: {
    title: 'Balconies',
    description: 'New, altered, or replacement balconies, terraces, and raised platforms.',
  },
  'structural-alterations': {
    title: 'Structural alterations',
    description: 'Structural changes such as wall removal, new openings, lintels, or repairs.',
  },
  roofing: {
    title: 'Roofing',
    description: 'Roof replacement, roof alterations, roof repairs, and roof structure works.',
  },
  'internal-refurbishments': {
    title: 'Internal refurbishments',
    description: 'Internal refurbishment of existing buildings and premises.',
  },
  kitchens: {
    title: 'Kitchens',
    description: 'Kitchen installation, replacement, remodelling, and kitchen fit-out works.',
  },
  bathrooms: {
    title: 'Bathrooms',
    description: 'Bathroom installation, replacement, remodelling, and bathroom fit-out works.',
  },
  'changing-rooms': {
    title: 'Changing rooms',
    description: 'Changing rooms, shower facilities, and associated welfare areas.',
  },
  'laundry-facilities': {
    title: 'Laundry facilities',
    description: 'Laundry rooms, launderettes, and laundry service facilities.',
  },
  lighting: {
    title: 'Lighting',
    description: 'Internal, external, sports, security, or amenity lighting works.',
  },
  glazing: {
    title: 'Glazing',
    description: 'Windows, doors, rooflights, dormers, bifolds, conservatories, and glazing.',
  },
  'shop-fronts': {
    title: 'Shop fronts',
    description: 'Shopfronts, retail frontages, shutters, signage fascias, and entrance fronts.',
  },
  groundworks: {
    title: 'Groundworks',
    description: 'Ground preparation, foundations, earthworks, and enabling site works.',
  },
  landscaping: {
    title: 'Landscaping',
    description: 'Hard and soft landscaping, planting, garden works, and external layouts.',
  },
  drainage: {
    title: 'Drainage',
    description: 'Drainage works, surface water drainage, soakaways, and related infrastructure.',
  },
  'fencing-boundaries': {
    title: 'Fencing / boundaries',
    description: 'Fences, walls, gates, boundary treatments, and enclosures.',
  },
  'sewage-treatment': {
    title: 'Sewage treatment',
    description:
      'Septic tanks, cesspits, package treatment plants, private drainage, and sewage systems.',
  },
  'indoor-play-areas': {
    title: 'Indoor play areas',
    description: 'Indoor play centres, soft play areas, and internal play facilities.',
  },
  'outdoor-play-areas': {
    title: 'Outdoor play areas',
    description: 'Outdoor play areas, play equipment, and playground facilities.',
  },
  'swimming-pools': {
    title: 'Swimming pools',
    description: 'Indoor and outdoor swimming pools, pool enclosures, and pool facilities.',
  },
  'skate-parks': {
    title: 'Skate parks',
    description: 'Skate parks, skate ramps, wheeled sports areas, and associated features.',
  },
  'sports-fields': {
    title: 'Sports fields',
    description: 'Sports pitches, playing fields, athletic tracks, and outdoor sport areas.',
  },
  'sports-courts': {
    title: 'Sports courts',
    description: 'Tennis, padel, basketball, netball, and multi-use sports courts.',
  },
  pavilions: {
    title: 'Pavilions',
    description: 'Pavilions, clubhouses, sports shelters, and recreation outbuildings.',
  },
  'renewables-solar': {
    title: 'Solar',
    description: 'Solar panels, solar arrays, and related solar equipment.',
  },
  'renewables-ev-charging': {
    title: 'EV charging',
    description: 'Electric vehicle charging points and associated infrastructure.',
  },
  'renewables-heat-pump': {
    title: 'Heat pumps',
    description: 'Air-source, ground-source, and water-source heat pump installations.',
  },
  'renewables-battery-storage': {
    title: 'Battery storage',
    description: 'Battery storage units, energy storage systems, and associated compounds.',
  },
}

export const planningClassificationGroups: PlanningClassificationGroupConfig[] = [
  {
    id: 'homes',
    title: 'Homes',
    searchPlaceholder: 'Search home types',
    options: ['houses', 'flats-apartments', 'hmos'],
  },
  {
    id: 'education',
    title: 'Education',
    searchPlaceholder: 'Search education types',
    options: [
      'schools',
      'training-centres',
      'colleges',
      'universities',
      'kindergartens-nurseries-creche',
    ],
  },
  {
    id: 'community-amenity',
    title: 'Community / amenity',
    searchPlaceholder: 'Search community types',
    options: [
      'community-centres',
      'libraries',
      'civic-government-buildings',
      'religious-buildings',
      'emergency-justice-buildings',
      'museums',
      'galleries',
      'public-toilets',
    ],
  },
  {
    id: 'health',
    title: 'Health',
    searchPlaceholder: 'Search health types',
    options: [
      'hospitals',
      'health-clinics-surgeries',
      'dental-clinics-surgeries',
      'care-homes',
      'medical-research-facilities',
      'veterinary-clinics',
      'pharmacies',
    ],
  },
  {
    id: 'commercial',
    title: 'Commercial',
    searchPlaceholder: 'Search commercial types',
    options: [
      'shops',
      'shopping-centres',
      'supermarkets',
      'offices',
      'banks-building-societies',
      'showrooms',
    ],
  },
  {
    id: 'hotels-leisure',
    title: 'Hotels / leisure',
    searchPlaceholder: 'Search leisure types',
    options: [
      'hotels',
      'guest-houses',
      'restaurants',
      'cafes',
      'fast-food',
      'pubs-bars',
      'night-clubs',
      'gyms',
      'leisure-centres',
      'salons-barbers',
      'games-venues',
      'conference-exhibition-venues',
    ],
  },
  {
    id: 'outdoor',
    title: 'Outdoor',
    searchPlaceholder: 'Search outdoor types',
    options: ['stadiums', 'outdoor-recreational-areas', 'camp-sites-holiday-villages'],
  },
  {
    id: 'agricultural-industrial',
    title: 'Agricultural / industrial',
    searchPlaceholder: 'Search industrial types',
    options: [
      'farms',
      'animal-buildings',
      'warehouses-storage-buildings',
      'workshops',
      'factories',
      'industrial-units',
      'distribution-depots',
      'vehicle-maintenance',
      'data-centres',
      'cold-stores-freezing-plants',
    ],
  },
]

export const planningCategoryGroups: PlanningCategoryGroupConfig[] = [
  {
    id: 'core-building',
    title: 'Core building works',
    options: [
      'conversions',
      'extensions',
      'new-build',
      'exterior-finish',
      'balconies',
      'structural-alterations',
      'roofing',
      'internal-refurbishments',
    ],
  },
  {
    id: 'fit-out',
    title: 'Fit-out / internal works',
    options: ['kitchens', 'bathrooms', 'changing-rooms', 'laundry-facilities', 'lighting'],
  },
  {
    id: 'envelope-frontage',
    title: 'Envelope / frontage',
    options: ['glazing', 'shop-fronts'],
  },
  {
    id: 'site-utilities',
    title: 'Site / utilities',
    options: ['groundworks', 'landscaping', 'drainage', 'fencing-boundaries', 'sewage-treatment'],
  },
  {
    id: 'recreational-facilities',
    title: 'Recreational facilities',
    options: [
      'indoor-play-areas',
      'outdoor-play-areas',
      'swimming-pools',
      'skate-parks',
      'sports-fields',
      'sports-courts',
      'pavilions',
    ],
  },
  {
    id: 'renewables',
    title: 'Renewables',
    options: [
      'renewables-solar',
      'renewables-ev-charging',
      'renewables-heat-pump',
      'renewables-battery-storage',
    ],
  },
]

const renewablesCategories: PlanningCategory[] = [
  'renewables-solar',
  'renewables-ev-charging',
  'renewables-heat-pump',
  'renewables-battery-storage',
]

const allCompanyTypePlanningClassifications = [...allPlanningClassifications]

const homeCompanyTypePlanningClassifications: PlanningClassification[] = [
  'houses',
  'flats-apartments',
  'hmos',
]

export const planningClassificationsByCompanyTypeMap: Record<
  CompanyType,
  PlanningClassification[]
> = {
  builder: allCompanyTypePlanningClassifications,
  glazier: allCompanyTypePlanningClassifications,
  flooring: allCompanyTypePlanningClassifications,
  kitchen: homeCompanyTypePlanningClassifications,
  retail: allCompanyTypePlanningClassifications,
  landscaper: allCompanyTypePlanningClassifications,
  renewables: allCompanyTypePlanningClassifications,
}

export const planningCategoriesByCompanyTypeMap: Record<CompanyType, PlanningCategory[]> = {
  builder: allPlanningCategories.filter(Boolean),
  glazier: ['glazing', 'conversions', 'extensions', 'new-build', 'shop-fronts'],
  flooring: ['conversions', 'extensions', 'new-build', 'internal-refurbishments'],
  kitchen: ['conversions', 'extensions', 'new-build', 'internal-refurbishments', 'kitchens'],
  retail: allPlanningCategories.filter(Boolean),
  landscaper: [
    'groundworks',
    'landscaping',
    'drainage',
    'fencing-boundaries',
    'sewage-treatment',
    'outdoor-play-areas',
    'swimming-pools',
    'skate-parks',
    'sports-fields',
    'sports-courts',
  ],
  renewables: withCategories(renewablesCategories, [
    'conversions',
    'extensions',
    'new-build',
    'roofing',
  ]),
}
